/**
 * THIS FILE IS NOT OK
 *
 * Needs a big refactoring. But at least now this mess is stowed away into its own file.
 */
import { useEntryFinder } from '~Components/EntryFinder';
import { useSelector, useI18n } from '~Hooks';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import _, { uniq } from 'lodash';

import { CompleteBongQuery_venue as Venue } from '~Scenes/POS/Queries/__queries__';

export type ItemCategoryMap = Record<
  string,
  {
    id: string;
    name: string;
    color: string;
    ukTakeAwayVatRate: number | null;
    order: number;
  } | null
>;
interface LeftItem {
  id: string;
  quantity: number;
  outOfStock: boolean;
}

const useCategories = (
  venue: Venue | null,
  inventoryItems: LeftItem[],
  initialCategoryId?: string,
) => {
  // Redux state
  const demoMode = useSelector((state) => state.demoMode);
  let POSLayout = useSelector((state) => state.POSLayout);
  if (demoMode) {
    POSLayout = window.DEMO_MODE_LAYOUT_ID;
  }

  // React state
  const [selectedCategory, setSelectedCategory] = useState<{ id: string; name: string } | null>(
    null,
  );

  // Custom hooks
  const findEntryFromItem = useEntryFinder();
  const { i18n } = useI18n();

  const layout = useMemo(() => {
    if (!POSLayout || POSLayout === 'NONE') {
      return null;
    }

    return venue?.company.layouts.find((l) => l.id === POSLayout);
  }, [venue, POSLayout]);

  const allCategories = useMemo(() => {
    return (
      venue?.company.categories.filter((category) =>
        category.items.some((item) => findEntryFromItem(item.id)),
      ) ?? []
    );
  }, [venue, findEntryFromItem]);

  const allItems = useMemo(() => {
    return _.flatMap(allCategories, (category) => category.items);
  }, [allCategories.length]);

  /**
   * Get categories / posCategories
   */
  const layoutCategories = useMemo(() => {
    if (!layout) return [];

    return layout.categories
      .filter((category) => {
        // If any button has offerId or pageId we return the category.
        const hasSpecialButton = category.page.buttons?.some((btn) => btn.offerId || btn.pageId);
        if (hasSpecialButton) return true;

        // Check if the page has any button that has a matching menu entry from the current menu
        const hasItemButtons = category.page.buttons?.some(
          (button) => button.itemId && findEntryFromItem(button.itemId),
        );

        // Include the category if there is a matching item.
        return hasItemButtons;
      })
      .map((category) => ({
        ...category,
        name: i18n(category.name),
      }))
      .sort((a, b) => (a.name < b.name ? -1 : 1))
      .sort((a, b) => b.order - a.order);
  }, [layout, findEntryFromItem, allItems]);

  const categories = useMemo(() => {
    return allCategories
      .map((category) => ({
        ...category,
        name: i18n(category.nameLang),
      }))
      .sort((a, b) => (a.name < b.name ? -1 : 1))
      .sort((a, b) => b.order - a.order);
  }, [allCategories]);

  useEffect(() => {
    if (layout) {
      // Guard: layoutCategories may be empty while data is still loading
      if (!layoutCategories.length) return;

      // If an initial category is provided and valid within the layout, restore it
      const restoredLayoutCat =
        initialCategoryId && layoutCategories.find((c) => c.id === initialCategoryId);
      setSelectedCategory(
        restoredLayoutCat
          ? { id: restoredLayoutCat.id, name: restoredLayoutCat.name }
          : { id: layoutCategories[0].id, name: layoutCategories[0].name },
      );

      return;
    }

    // Guard: allCategories may be empty while data is still loading — don't set invalid state
    if (!allCategories.length) return;

    // If the category ID is invalid, reselect
    if (!allCategories.find((category) => category.id === selectedCategory?.id)) {
      // Restore the initial category if provided and valid, otherwise fall back to first
      const restoredCat =
        initialCategoryId && allCategories.find((c) => c.id === initialCategoryId);
      setSelectedCategory(
        restoredCat
          ? { id: restoredCat.id, name: i18n(restoredCat.nameLang) }
          : { id: allCategories[0].id, name: i18n(allCategories[0].nameLang) },
      );
    }

    // If we have no selection and there's categories, reselect
    if (!selectedCategory && categories && !!categories.length) {
      // Restore the initial category if provided and valid, otherwise fall back to first
      const restoredCat = initialCategoryId && categories.find((c) => c.id === initialCategoryId);
      setSelectedCategory(
        restoredCat
          ? { id: restoredCat.id, name: restoredCat.name }
          : { id: categories[0].id, name: categories[0].name },
      );
    }
  }, [layout, categories]);

  const itemToCategoryMap = useMemo(() => {
    if (!allCategories) {
      return {};
    }

    return allCategories.reduce((obj, cat) => {
      cat.items.forEach((item) => {
        obj[item.id] = {
          id: cat.id,
          name: i18n(cat.nameLang),
          ukTakeAwayVatRate: cat.ukVatRate,
          color: cat.color,
          order: cat.order,
        };
      });

      return obj;
    }, {} as ItemCategoryMap);
  }, [allCategories]);

  const currentCategory = selectedCategory
    ? categories.find((c) => c.id === selectedCategory.id)
    : categories[0];

  const layoutCategory =
    layout && selectedCategory
      ? layoutCategories.find((c) => c.id === selectedCategory.id)
      : layoutCategories[0];

  const categoriesItems = _.flatMap(allCategories, (category) =>
    category.items.map((item) => item.id),
  ).filter(findEntryFromItem);

  const categoriesItemsIds: string[] = uniq(categoriesItems);

  const mapItem = useCallback(
    (item: any) => {
      const inventory = inventoryItems.find((invItem) => invItem.id === item.id);
      const color = allCategories.find((cat) => cat.items.find((i) => i.id === item.id))?.color;

      return {
        id: item.id,
        itemId: item.id,
        title: i18n(item.titleLang),
        color,
        vatRate: item.vatRate,
        descriptionLang: item.descriptionLang,
        imageUrl: item.image?.file?.url,
        quantity: inventory?.quantity,
        manualPrice: item.manualPrice,
        manualName: item.manualName,
        volumeSuffix: item.volumeSuffix,
        isOutOfStock: inventory ? inventory.outOfStock : false,
      };
    },
    [inventoryItems, allCategories],
  );

  const categoryItems = currentCategory
    ? currentCategory.items
        .filter((item: { id: string }) => findEntryFromItem(item.id))
        .map(mapItem)
    : [];

  return {
    mapItem,
    allItems,
    layout,
    layoutCategory,
    categories: layout ? layoutCategories : categories,
    selectedCategory,
    itemToCategoryMap,
    categoryItems,
    categoriesItemsIds,
    setSelectedCategory: setSelectedCategory,
  };
};

export default useCategories;
